import React, { useState, useEffect } from 'react';
import { contentAPI } from '../services/api';
import { FileText, Plus, Edit, Trash2, Download, Search, Users, X, Upload } from 'lucide-react';

export default function ContentManager() {
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content_type: 'brochure',
    department: 'Sales',
    is_public: 1,
    file: null
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchContent();
  }, [typeFilter]);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const params = {
        type: typeFilter !== 'all' ? typeFilter : undefined
      };

      const response = await contentAPI.getAll(params);
      
      if (response.data.success) {
        setContent(response.data.content);
      }
    } catch (error) {
      console.error('Failed to fetch content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (window.confirm(`Are you sure you want to delete "${title}"?`)) {
      try {
        await contentAPI.delete(id);
        fetchContent();
        alert('Content deleted successfully');
      } catch (error) {
        alert('Failed to delete content: ' + error.message);
      }
    }
  };

  const handleEdit = (item) => {
    setEditingContent(item);
    setFormData({
      title: item.title,
      description: item.description || '',
      content_type: item.content_type,
      department: item.department || 'Sales',
      is_public: item.is_public,
      file: null
    });
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditingContent(null);
    setFormData({
      title: '',
      description: '',
      content_type: 'brochure',
      department: 'Sales',
      is_public: 1,
      file: null
    });
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        file: file
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.content_type) {
      alert('Title and Content Type are required');
      return;
    }

    if (!editingContent && !formData.file) {
      alert('Please select a file to upload');
      return;
    }

    try {
      setSaving(true);
      
      if (editingContent) {
        // Update existing content (without file upload for now)
        await contentAPI.update(editingContent.id, {
          title: formData.title,
          description: formData.description,
          content_type: formData.content_type,
          department: formData.department,
          is_public: formData.is_public
        });
        alert('Content updated successfully');
      } else {
        // Create new content with file upload
        const submitData = new FormData();
        submitData.append('title', formData.title);
        submitData.append('description', formData.description);
        submitData.append('content_type', formData.content_type);
        submitData.append('department', formData.department);
        submitData.append('is_public', formData.is_public);
        submitData.append('file', formData.file);
        
        const response = await fetch('/api/content', {
          method: 'POST',
          body: submitData
        });
        
        const result = await response.json();
        
        if (!response.ok) {
          throw new Error(result.error || 'Upload failed');
        }
        
        alert('Content created successfully');
      }
      
      setShowModal(false);
      fetchContent();
    } catch (error) {
      alert('Failed to save content: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      brochure: 'bg-blue-100 text-blue-800',
      product: 'bg-green-100 text-green-800',
      case_study: 'bg-teal-100 text-teal-800',
      video: 'bg-red-100 text-red-800',
      document: 'bg-[var(--soft)] text-[var(--text)]'
    };
    return colors[type] || 'bg-[var(--soft)] text-[var(--text)]';
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'brochure': return '📄';
      case 'product': return '📦';
      case 'case_study': return '📊';
      case 'video': return '🎥';
      default: return '📁';
    }
  };

  const filteredContent = content.filter(item => 
    item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Add this function before return statement (around line 170)
const handleViewContent = (item) => {
  const fileExt = item.file_url.split('.').pop().toLowerCase();
  
  // Define file type categories
  const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
  const pdfTypes = ['pdf'];
  const docTypes = ['doc', 'docx'];
  const pptTypes = ['ppt', 'pptx'];
  const videoTypes = ['mp4', 'mov', 'avi', 'mkv'];
  
  if (imageTypes.includes(fileExt) || pdfTypes.includes(fileExt)) {
    // Images and PDFs - Open in new tab (browser can display these)
    window.open(item.file_url, '_blank');
  } else if (docTypes.includes(fileExt) || pptTypes.includes(fileExt)) {
    // Office documents - Use Google Docs Viewer
    const viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(item.file_url)}&embedded=true`;
    window.open(viewerUrl, '_blank');
  } else if (videoTypes.includes(fileExt)) {
    // Videos - Open in new tab (browser has video player)
    window.open(item.file_url, '_blank');
  } else {
    // Unknown type - Direct download
    const link = document.createElement('a');
    link.href = item.file_url;
    link.download = item.title;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

const getFileTypeLabel = (fileUrl) => {
  if (!fileUrl) return 'Unknown';
  const ext = fileUrl.split('.').pop().toLowerCase();
  const typeMap = {
    pdf: 'PDF',
    doc: 'Word',
    docx: 'Word',
    ppt: 'PowerPoint',
    pptx: 'PowerPoint',
    jpg: 'Image',
    jpeg: 'Image',
    png: 'Image',
    gif: 'Image',
    mp4: 'Video',
    mov: 'Video'
  };
  return typeMap[ext] || ext.toUpperCase();
};
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[var(--text)]">Content Library</h1>
          <p className="text-[var(--muted)] mt-1">Manage brochures, products, and marketing materials</p>
        </div>
        <button 
          onClick={handleCreate}
          className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center space-x-2"
        >
          <Plus size={20} />
          <span>Add Content</span>
        </button>
      </div>

      {/* Filters & Search */}
      <div className="bg-[var(--card)] rounded-xl shadow-md p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search content by title..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-[var(--border)] rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </div>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-4 py-2 border border-[var(--border)] rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          >
            <option value="all">All Types</option>
            <option value="brochure">Brochures</option>
            <option value="product">Products</option>
            <option value="case_study">Case Studies</option>
            <option value="video">Videos</option>
            <option value="document">Documents</option>
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-[var(--card)] rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[var(--muted)]">Total Content</p>
              <p className="text-2xl font-bold text-[var(--text)]">{content.length}</p>
            </div>
            <FileText className="text-teal-500" size={32} />
          </div>
        </div>
        <div className="bg-[var(--card)] rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[var(--muted)]">Brochures</p>
              <p className="text-2xl font-bold text-blue-600">
                {content.filter(c => c.content_type === 'brochure').length}
              </p>
            </div>
            <span className="text-3xl">📄</span>
          </div>
        </div>
        <div className="bg-[var(--card)] rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[var(--muted)]">Products</p>
              <p className="text-2xl font-bold text-green-600">
                {content.filter(c => c.content_type === 'product').length}
              </p>
            </div>
            <span className="text-3xl">📦</span>
          </div>
        </div>
        <div className="bg-[var(--card)] rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[var(--muted)]">Case Studies</p>
              <p className="text-2xl font-bold text-teal-600">
                {content.filter(c => c.content_type === 'case_study').length}
              </p>
            </div>
            <span className="text-3xl">📊</span>
          </div>
        </div>
      </div>

      {/* Content Grid */}
      <div className="bg-[var(--card)] rounded-xl shadow-md p-6">
        {loading ? (
          <div className="text-center py-12">
            <div className="text-[var(--muted)]">Loading content...</div>
          </div>
        ) : filteredContent.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-[var(--muted)]">No content found</p>
            <button
              onClick={handleCreate}
              className="mt-4 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
            >
              Create First Content
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredContent.map((item) => (
              <div key={item.id} className="border border-[var(--border)] rounded-lg p-4 hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getTypeColor(item.content_type)}`}>
                    {getTypeIcon(item.content_type)} {item.content_type.replace('_', ' ')}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs ${
                    item.is_public ? 'bg-green-100 text-green-700' : 'bg-[var(--soft)] text-[var(--text)]'
                  }`}>
                    {item.is_public ? 'Public' : 'Private'}
                  </span>
                </div>

                <div className="mb-3 bg-[var(--soft)] rounded-lg h-32 flex items-center justify-center">
                  {item.thumbnail_url ? (
                    <img src={item.thumbnail_url} alt={item.title} className="h-full w-full object-cover rounded-lg" />
                  ) : (
                    <span className="text-5xl">{getTypeIcon(item.content_type)}</span>
                  )}
                </div>

                <h3 className="font-bold text-[var(--text)] mb-2 line-clamp-2">{item.title}</h3>
                <p className="text-sm text-[var(--muted)] mb-3 line-clamp-2">
                  {item.description || 'No description'}
                </p>

                <div className="flex items-center justify-between text-sm text-[var(--muted)] mb-4">
                  <div className="flex items-center space-x-4">
                    <span className="flex items-center">
                      <Download size={14} className="mr-1" />
                      {item.download_count || 0}
                    </span>
                    <span className="flex items-center">
                      <Users size={14} className="mr-1" />
                      {item.assigned_employees || 0}
                    </span>
                  </div>
                  {item.department && (
                    <span className="text-xs bg-[var(--soft)] px-2 py-1 rounded">
                      {item.department}
                    </span>
                  )}
                </div>

                <div className="flex space-x-2">
                 {item.file_url && (
  <button
    onClick={() => handleViewContent(item)}
    className="flex-1 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-center text-sm flex items-center justify-center space-x-1"
  >
    <span>View</span>
    <span className="text-xs opacity-75">({getFileTypeLabel(item.file_url)})</span>
  </button>
)}
                  <button
                    onClick={() => handleEdit(item)}
                    className="px-3 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(item.id, item.title)}
                    className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Content Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--card)] rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">
                {editingContent ? 'Edit Content' : 'Add New Content'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-[var(--muted)]"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-[var(--text)] mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g., OneWealth Insurance Brochure"
                  className="w-full px-4 py-2 border border-[var(--border)] rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-[var(--text)] mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Brief description of the content..."
                  className="w-full px-4 py-2 border border-[var(--border)] rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              {/* Content Type & Department */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--text)] mb-2">
                    Content Type *
                  </label>
                  <select
                    name="content_type"
                    value={formData.content_type}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-[var(--border)] rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    required
                  >
                    <option value="brochure">Brochure</option>
                    <option value="product">Product</option>
                    <option value="case_study">Case Study</option>
                    <option value="video">Video</option>
                    <option value="document">Document</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--text)] mb-2">
                    Department
                  </label>
                  <select
                    name="department"
                    value={formData.department}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-[var(--border)] rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="Sales">Sales</option>
                    <option value="Marketing">Marketing</option>
                    <option value="Operations">Operations</option>
                    <option value="HR">HR</option>
                  </select>
                </div>
              </div>

              {/* File Upload */}
              {!editingContent && (
                <div>
                  <label className="block text-sm font-medium text-[var(--text)] mb-2">
                    Upload File *
                  </label>
                  <div className="border-2 border-dashed border-[var(--border)] rounded-lg p-6 text-center hover:border-teal-500 transition-colors">
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.ppt,.pptx,.mp4"
                      onChange={handleFileChange}
                      className="hidden"
                      id="file-upload"
                      required={!editingContent}
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <Upload className="mx-auto text-gray-400 mb-2" size={32} />
                      <p className="text-sm text-[var(--muted)] mb-1">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-xs text-[var(--muted)]">
                        PDF, Images, Documents, Videos (Max 50MB)
                      </p>
                    </label>
                  </div>
                  {formData.file && (
                    <div className="mt-3 p-3 bg-teal-50 rounded-lg flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <FileText className="text-teal-600" size={20} />
                        <div>
                          <p className="text-sm font-medium text-[var(--text)]">{formData.file.name}</p>
                          <p className="text-xs text-[var(--muted)]">
                            {(formData.file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, file: null }))}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X size={20} />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {editingContent && (
                <div className="p-4 bg-yellow-50 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> File upload for editing will be implemented soon. Currently you can only update title, description, and settings.
                  </p>
                </div>
              )}

              {/* Public Checkbox */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="is_public"
                  checked={formData.is_public === 1}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500"
                  id="is-public"
                />
                <label htmlFor="is-public" className="ml-2 text-sm font-medium text-[var(--text)]">
                  Make this content public (visible to all employees)
                </label>
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-[var(--border)] rounded-lg hover:bg-[var(--soft)] disabled:opacity-50"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center space-x-2"
                  disabled={saving}
                >
                  {saving && (
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  )}
                  <span>{saving ? 'Saving...' : (editingContent ? 'Update Content' : 'Create Content')}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}